import React from "react";
import * as Sentry from "@sentry/react";
//#region types
import { AgentConversation } from "utils/helpers/agentConversationStore";
import { AgentMode } from "./agentFlow";
//#endregion

export type AgentPopperStep = AgentMode | "faq";

export const popperStepOf = (mode: AgentMode, agentUnavailable: boolean): AgentPopperStep =>
  agentUnavailable ? "faq" : mode;

// The popper's content mounts when it opens and unmounts when it closes, so its lifetime is the
// popper's. A page reload or a closed tab is a close too (`page_left`); the conversation goes on.
const useAgentPopperTracking = (
  conversation: AgentConversation,
  active: boolean,
  step: AgentPopperStep,
  conversationId: string
) => {
  const stepRef = React.useRef(step);
  stepRef.current = step;

  // Follows the id, so errors after "Ny fråga" carry the new conversation's.
  React.useEffect(() => {
    if (active) Sentry.setTag("agentConversationId", conversationId);
  }, [active, conversationId]);

  React.useEffect(() => {
    if (!active) return;

    conversation.popperOpened();

    let pageLeft = false;
    const onPageHide = () => {
      pageLeft = true;
      conversation.track("popper.closed", { reason: "page_left", step: stepRef.current });
      // Sentry sends its log buffer when the page turns hidden, which comes before pagehide.
      void Sentry.flush();
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      if (!pageLeft) conversation.track("popper.closed", { reason: "closed", step: stepRef.current });
      Sentry.setTag("agentConversationId", undefined);
    };
  }, [active, conversation]);
};

export default useAgentPopperTracking;
